/**
 * Pure helpers for the rules form's action values, kept out of `rules-form-config`
 * because that module also carries the query-builder's JSX controls — importing it
 * drags in `@react-querybuilder/material` and the `src/components` barrel.
 *
 * The enums come from their defining modules rather than the `src/graphql` barrel on
 * purpose: `behavior.mappings` imports `rules-form-config`, so going through the
 * barrel puts this file inside that cycle and the enums read as `undefined` at import
 * time under vitest.
 */
import { stripHtmlTags } from '@provision/formatting-utils';
import { ActionType } from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import { SendConversationMessageSource } from 'src/graphql/entities/behavior/behavior.types';

interface DependentActionField {
  dependsOn?: { attribute: string; value: string }[];
  fieldOptions?: { value: string; label: string }[];
}

/**
 * Narrows an action's config fields to the ones currently visible. A field carrying
 * `dependsOn` shows only while one of the named attributes holds the named value;
 * `dependsOn` and `fieldOptions` are stripped from what comes back, since they are
 * config metadata the form control itself has no use for.
 *
 * The comparison accepts either shape a form value can take: list and autocomplete
 * controls store the whole selected option, a toggle group stores the bare value.
 * Reading `.value` unconditionally — as this did — silently hides every field that
 * depends on a control of the second kind.
 */
export function getVisibleActionFields<T>(fields: T[], value: unknown): T[] {
  const formValue = (value ?? {}) as Record<string, unknown>;

  return fields.reduce<T[]>((acc, field) => {
    const { fieldOptions, dependsOn, ...rest } = field as T & DependentActionField;

    if (!dependsOn) {
      return [...acc, rest as T];
    }

    const isVisible = dependsOn.some((dep) => {
      if (!(dep.attribute in formValue)) {
        return false;
      }

      const dependentValue = formValue[dep.attribute];
      const resolved =
        dependentValue && typeof dependentValue === 'object'
          ? (dependentValue as { value?: unknown }).value
          : dependentValue;

      return dep.value === resolved;
    });

    return isVisible ? [...acc, rest as T] : acc;
  }, []);
}

/**
 * The selected source's body is required; the branch that is hidden is not. Returns
 * the message to show rather than a boolean, so the action can say *which* field is
 * missing — this is wired as a `validate` rule on the action's `Controller`, which
 * means a failed save marks it and, per react-hook-form's default
 * `reValidateMode: 'onChange'`, typing clears it.
 *
 * Only the send-message action is checked. Expanding this to the rest of the form is
 * 1V-2122's job, and doing it here would change how every other action saves.
 */
export function validateActionFormValue(
  actionType: ActionType,
  value: unknown,
): string | true {
  if (
    actionType !== ActionType.SendConversationMessage ||
    !value ||
    typeof value !== 'object'
  ) {
    return true;
  }

  const { messageSource, message, aiPrompt } = value as {
    messageSource?: string;
    message?: string;
    aiPrompt?: string;
  };

  if (messageSource === SendConversationMessageSource.AiPrompt) {
    return aiPrompt?.trim() ? true : 'Prompt is required when the message is AI-written.';
  }

  return hasRichTextContent(message ?? '') ? true : 'Message is required.';
}

/**
 * Elements that are content in their own right even though they leave no text once
 * the tags come off. `br` is deliberately absent: `<p><br></p>` is what an untouched
 * editor submits, so counting it would make every empty message look filled.
 */
const CONTENT_BEARING_TAGS = /<(img|hr|video|audio|iframe|embed|object|table)\b/i;

/**
 * Rich text is never truly empty — an untouched editor still submits its wrapper
 * markup — so emptiness is judged on the text left after the tags come off. An
 * image-only or rule-only message has no such text but is not empty, and treating it
 * as empty would leave an existing rule unsaveable.
 */
function hasRichTextContent(html: string): boolean {
  return Boolean(stripHtmlTags(html).trim()) || CONTENT_BEARING_TAGS.test(html);
}
